import { Middleware, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { useDispatch } from 'react-redux';
import loadingReducer, {
  NOT_LOADING_APIS,
  closeLoading,
  openLoading,
} from '~/store/loading';
import userReducer from '~/store/user';
import resourceReducer from '~/store/resource';
import internetResourceReducer from '~/store/internetResource';
import recordReducer from '~/store/record';
import notifyReducer from '~/store/notify';
import fileReducer from '~/store/file';
import aiAssistantReducer from '~/store/aiAssistant';
import { oneClubApi } from '~/api/oneClub';
import { onePassApi } from '~/api/onePass';
import { oneBoxV2Api } from '~/api/oneBoxV2';
import { oneBoxV3Api } from '~/api/oneBoxV3';
import { MOELoginApi } from '~/api/MOELogin';
import { downloadCompleteMiddleware } from '~/tracking/downloadCompleteMiddleware';
import { rtkQueryErrorLogger } from '~/utils/sentry';

type TMiddlewareAction<M = unknown, P = unknown> = {
  meta: { arg: { endpointName: string } } & M;
  payload: P;
  type: string;
};

const loadingMiddleware: Middleware =
  store => next => (action: TMiddlewareAction) => {
    // next => 將 action 交給下個 middleware 的 func.
    const isNeedLoading = () => {
      if (action.type && action.meta) {
        return !NOT_LOADING_APIS.find(
          item =>
            action.type.startsWith(item.api) &&
            action.meta.arg.endpointName === item.endpointName,
        );
      }
      return false;
    };

    if (action.type.endsWith('/pending') && isNeedLoading()) {
      // store.getState() // 透過 store 去 get current state
      store.dispatch(openLoading());
    } else if (
      action.type.endsWith('/fulfilled') ||
      action.type.endsWith('/rejected')
    ) {
      store.dispatch(closeLoading());
      // 透過 store.dispatch(...) 來發送的 action 會重新經過所有 middleware
      // 但 call next(action) 只會送到下一個 middleware
    }
    return next(action);
    // 每個 middleware 接到的 action 都是前一個 middleware call next func. 將 action (next(action)) 交給他的
    // 如果沒有 next(action) （最後一個 middleware） 的話就交給 reducer 處理
    // 最後一個 middleware call next 所 return 的 value 是被傳入的 action
  };

export const store = configureStore({
  reducer: {
    loading: loadingReducer,
    user: userReducer,
    resource: resourceReducer,
    internetResource: internetResourceReducer,
    record: recordReducer,
    notify: notifyReducer,
    file: fileReducer,
    aiAssistant: aiAssistantReducer,
    [oneClubApi.reducerPath]: oneClubApi.reducer,
    [onePassApi.reducerPath]: onePassApi.reducer,
    [oneBoxV2Api.reducerPath]: oneBoxV2Api.reducer,
    [oneBoxV3Api.reducerPath]: oneBoxV3Api.reducer,
    [MOELoginApi.reducerPath]: MOELoginApi.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        // ignoredActions: ['your/action/type'],
        // Ignore these field paths in all actions
        ignoredActionPaths: [
          'meta.baseQueryMeta.request',
          'meta.baseQueryMeta.response',
          'payload',
        ],
        // Ignore these paths in the state
        ignoredPaths: ['resource.progressList'],
      },
    })
      .concat(loadingMiddleware)
      .concat(downloadCompleteMiddleware)
      .concat(rtkQueryErrorLogger)
      .concat(oneClubApi.middleware)
      .concat(onePassApi.middleware)
      .concat(oneBoxV2Api.middleware)
      .concat(oneBoxV3Api.middleware)
      .concat(MOELoginApi.middleware),
});

setupListeners(store.dispatch); // refetchOnFocus || refetchOnReconnect

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
